import React, { FC } from "react";

import { SanitizeHtml } from "@keepeek/refront-components";
import { AppBar, Box, ButtonBase, Toolbar, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";

import { CONFIGURATION_SECTION_PATH } from "../../components/layout/adminMenu";
import { toolBarHeight } from "../../components/layout/constants";
import { KHeaderWidget } from "../../models/configuration/components/kHeaderWidget";
import { Default } from "../../models/configuration/layouts/default";
import { Key, Theme } from "../../models/configuration/theme";
import { useConfiguration, usePageVisible } from "../../providers/config/hooks";
import { configSectionThemeAssetSelector } from "../../providers/config/selectors";
import { frontEditState } from "../../providers/frontEdit/atoms/frontEdit";
import { getCurrentContentEditsSelector } from "../../providers/frontEdit/selectors/currentContentEdits";
import { PagesPath } from "../App/utils";
import BasketToggler from "../Basket/BasketToggler";
import CurrentUserMenu from "../CurrentUserMenu";

const PREFIX = "DefaultAppBar";

const classes = {
  grow: `${PREFIX}-grow`,
  appBar: `${PREFIX}-appBar`,
  toolbar: `${PREFIX}-toolbar`,
  logoTitle: `${PREFIX}-logoTitle`,
  logo: `${PREFIX}-logo`,
  button: `${PREFIX}-button`,
  buttonTitle: `${PREFIX}-buttonTitle`,
  buttonHome: `${PREFIX}-buttonHome`,
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  [`& .${classes.grow}`]: {
    flexGrow: 1,
  },

  [`&.${classes.appBar}`]: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: "#fff",
  },

  [`& .${classes.toolbar}`]: {
    height: toolBarHeight,
    maxHeight: "10vh",
    minHeight: 0,
    paddingRight: 0,
  },

  [`& .${classes.logoTitle}`]: {
    padding: 0,
    margin: 0,
    height: "3rem",
    maxHeight: "8vh",
    maxWidth: "40vw",
  },

  [`& .${classes.logo}`]: {
    maxHeight: "100%",
    maxWidth: "100%",
  },

  [`& .${classes.button}`]: {
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.primary,
    alignSelf: "stretch",
    "& + &": {
      marginLeft: theme.spacing(2),
    },
    "&:focus-within": {
      outline: `1px solid ${theme.palette.primary.main}`,
    },
  },

  [`& .${classes.buttonTitle}`]: {
    textTransform: "none",
  },

  [`& .${classes.buttonHome}`]: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
}));

type DefaultAppBarProps = {
  position?: "fixed" | "absolute" | "relative" | "static" | "sticky" | undefined;
  hasBasket?: boolean;
};

// This is the Default App Bar
// We keep it only for configuration time or really simple ReFront
// We should now use the header widget (KHeaderWidget) instead for more advanced configuration
const DefaultAppBar: FC<React.PropsWithChildren<DefaultAppBarProps>> = function ({
  position = "relative",
  hasBasket = false,
}) {
  const { t } = useTranslation();

  // Logo main
  const logoMainQuery = useRecoilValueLoadable<string[]>(
    configSectionThemeAssetSelector(Key.ImageLogoMain),
  );
  const [logoMain] = logoMainQuery.state === "hasValue" ? logoMainQuery.contents : [undefined];
  const configTheme = useConfiguration<Theme>(CONFIGURATION_SECTION_PATH.THEME);
  let logoMainSrc = logoMain;
  if (configTheme?.logoMain?.url) {
    logoMainSrc = configTheme.logoMain.url;
  }

  const frontEdit = useRecoilValue(frontEditState);
  const currentContentEdit = useRecoilValue(
    getCurrentContentEditsSelector({
      endpoint: `${CONFIGURATION_SECTION_PATH.THEME}`,
    }),
  );
  const currentEditData =
    currentContentEdit && currentContentEdit.data ? currentContentEdit.data : null;

  const logoMainSrcFrontEdit = currentEditData?.logoMain?.url;
  // Links
  const defaultConfig = useConfiguration<Default>(CONFIGURATION_SECTION_PATH.LAYOUT_DEFAULT);
  const { homeLink, basketTogglerShowCount } = defaultConfig || {
    homeLink: true,
    basketTogglerShowCount: true,
  };
  const { homeVisible, elementVisible, searchVisible } = usePageVisible();

  // Remove this DefaultAppBar if we have a configured Header Widget
  // The link is volontary weak to insist on configuring a Header Widget
  const kHeaderWidgets = useConfiguration<KHeaderWidget[]>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_K_HEADER_WIDGET,
  );
  const { pathname } = useRouter();
  // Keep it in admin
  const isAdminPage = pathname.includes(PagesPath.ADMIN);
  if (!isAdminPage && kHeaderWidgets?.length !== 0 && kHeaderWidgets?.length !== undefined) {
    return null;
  }

  const logo = (
    <h1 className={classes.logoTitle} aria-label={`${t("app.title")}`}>
      {logoMainSrc && (!frontEdit || (frontEdit && !logoMainSrcFrontEdit)) && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          aria-hidden
          src={logoMainSrc}
          className={classes.logo}
          alt={t("app.title") as string}
        />
      )}
      {/* Preview of default logo on edit mode */}
      {frontEdit && logoMainSrcFrontEdit && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          aria-hidden
          src={logoMainSrcFrontEdit}
          className={classes.logo}
          alt={t("app.title") as string}
        />
      )}
    </h1>
  );

  return (
    <StyledAppBar position={position} className={classes.appBar} elevation={0}>
      <nav>
        <Toolbar className={classes.toolbar}>
          {homeVisible || elementVisible || searchVisible ? (
            <Link href="/home" data-testid="gotoHomePage" legacyBehavior>
              <a title={t("layout.homePageName.aria") as string}>{logo}</a>
            </Link>
          ) : (
            logo
          )}
          <div className={classes.grow} />
          {homeLink && homeVisible && (elementVisible || searchVisible) && (
            <Link href="/home" passHref data-testid="gotoHomePage" legacyBehavior>
              <ButtonBase
                tabIndex={-1}
                aria-hidden
                component="a"
                className={clsx(classes.button, classes.buttonHome)}
              >
                <Typography variant="button" className={classes.buttonTitle}>
                  <SanitizeHtml html={t("layout.app-bar.homepage-button.label")} />
                </Typography>
              </ButtonBase>
            </Link>
          )}
          <CurrentUserMenu cookieConsentManagement={true} />
          {hasBasket && (
            <Box>
              <BasketToggler showCount={basketTogglerShowCount} />
            </Box>
          )}
        </Toolbar>
      </nav>
    </StyledAppBar>
  );
};

export default DefaultAppBar;
